import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { ServicePermissionService } from '@/services/service-permission.service'
import { BehaviorSubject, Observable, Subject } from 'rxjs'

export interface TerminalStat {
  date: string
  join_count: number
  exit_count: number
}

export interface NetSpeed {
  stat_time: string
  upload_speed: number
  download_speed: number
}

export interface HomeData {
  team_stat: {
    total: number
    online_count: number
    offline_count: number
  }
  safe_stat: {
    virus: number
    sick: number
    high_upload: number
    high_download: number
    high_proportion_system_disk: number
    high_proportion_disk: number
    high_cpu_load: number
    high_dick_load: number
    high_cpu_temperature: number
    high_disk_temperature: number
    hardware_change: number
    low_version: number
  }
  terminal_stat: Array<TerminalStat>
  net_stat: Array<NetSpeed>
}

export interface SafeStatusRes {
  /** 最终结果，1 极好，2 良好，3 不好 */
  final_results: number
  /** 未处理病毒木马 */
  not_dealt_virus_count: number
  /** 病毒影响终端数 */
  virus_affect_count: number
  /** 未处理高危漏洞 */
  not_dealt_leak_count: number
  /** 漏洞影响终端数 */
  leak_affect_count: number
  /** 基线扫描不合规 */
  baseline_substandard_count: number
  /** 开放危险端口数 */
  danger_port_count: number
  /** 漏洞影响终端数 */
  port_affect_count: number
  /** 体检60以下 */
  physical_substandard_count: number
  /** 体检60-99分 */
  physical_standard_count: number
  /** 体检100分 */
  physical_perfect_count: number
  /** 团队分数 */
  score: number
  /** 未处理病毒木马 */
  last_not_dealt_virus_count: number
  /** 病毒影响终端数 */
  last_virus_affect_count: number
  /** 未处理高危漏洞 */
  last_not_dealt_leak_count: number
  /** 漏洞影响终端数 */
  last_leak_affect_count: number
  /** 基线扫描不合规 */
  last_baseline_substandard_count: number
  /** 上周开放危险端口数 */
  last_danger_port_count: number
  /** 上周漏洞影响终端数 */
  last_port_affect_count: number
}

export interface StatsTerminalRes {
  /** 已购买（已订阅服务） */
  buy_count: number
  /** 授权超限数量 */
  exceed_count: number
  /** 服务到期数量 */
  expire_count: number
  /** 已部署 */
  deploy_count: number
  /** 当前在线 */
  online_count: number
  /** 当前离线 */
  offline_count: number
  /** 今日加入 */
  today_join_count: number
  /** 今日离开 */
  today_leave_count: number
  /** 待审批数量 */
  peripherals_approval_count: number
  // 外发审批
  send_file_approval_count:number
  un_join_count:number
  // 终端数量是否超出限制
  expire_terminal_count: number
}

export interface RiskEventTrendRequestParams {
  /** 开始时间（Unix时间戳，秒） */
  start_time: string
  /** 结束时间（Unix时间戳，秒） */
  end_time: string
  /** 时间粒度 */
  granularity: string
}

export interface RiskEventTrendResponseData {
  /** 不合格检测项数量列表 */
  baseline_risk_counts?: number[]
  /** 漏洞风险数量列表 */
  leak_risk_counts?: number[]
  /** 端口风险数量列表 */
  port_risk_counts?: number[]
  /** 勒索风险数量列表 */
  ransomware_risk_counts?: number[]
  /** 时间点列表（Unix时间戳，秒） */
  time_points?: string[]
  /** 病毒风险数量列表 */
  virus_risk_counts?: number[]
}

export interface AuthorizationResponseData {
  /** 授权状态, 0-未授权,1-已授权,2-授权过期 */
  authorize_state: number
  /** 授权结束时间 */
  authorize_end_time: number
}
let timedRefresh = 0

@Injectable()
export class HomeService {
  timedRefresh: Observable<number>
  safeStatusData = {} as SafeStatusRes
  statsTerminalData = {} as StatsTerminalRes
  private refreshTimer: any = null
  private updateTimedRefreshEvent = new Subject<number>()
  updateSafeStatusDataEvent = new BehaviorSubject<SafeStatusRes>(this.safeStatusData)
  updateStatsTerminalDataEvent = new BehaviorSubject<StatsTerminalRes>(this.statsTerminalData)

  constructor (private http: Http) {
    this.timedRefresh = this.updateTimedRefreshEvent.asObservable()
  }

  // eslint-disable-next-line no-magic-numbers
  startRefreshInterval (timeout = 2 * 1000 * 60) {
    clearTimeout(this.refreshTimer)
    this.refreshIntervalStep(timeout)
  }

  stopRefreshInterval () {
    clearTimeout(this.refreshTimer)
  }

  // eslint-disable-next-line no-magic-numbers
  private refreshIntervalStep (timeout = 2 * 1000 * 60) {
    clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => {
      this.updateTimedRefresh(timedRefresh + 1)
      this.refreshIntervalStep(timeout)
    }, timeout)
  }

  updateTimedRefresh (timed: number) {
    timedRefresh = timed
    this.initSafeStatus()
    this.initStatsTerminal()
    this.updateTimedRefreshEvent.next(timed)
  }

  getData () {
    return this.http.post<HomeData>('/terminal/static', {}).then(res => {
      ['team_stat', 'safe_stat', 'terminal_stat', 'net_stat'].forEach(key => {
        res[key] = res[key] || {}
      })
      return res
    })
  }

  initSafeStatus () {
    this.http.post<SafeStatusRes>('/homepage/safe-status', {}).then((response) => {
      this.safeStatusData = response
      this.updateSafeStatusDataEvent.next(response)
    })
  }

  initStatsTerminal () {
    this.http.post<StatsTerminalRes>('/homepage/stats/terminal', {}).then((response) => {
      this.statsTerminalData = response
      this.updateStatsTerminalDataEvent.next(response)
    })
  }
  // 获取风险事件趋势
  getTerminalRiskEventTrend (data: RiskEventTrendRequestParams) {
    return this.http.post<RiskEventTrendResponseData>('/terminal/risk-event-trend', data)
  }
  /** 团队订阅服务授权到期信息（私有化） */
  getPermissionOrderAuthorization () {
    return this.http.post<AuthorizationResponseData>('/permission_order/authorization', {})
  }
}
